import { auth } from '@/lib/firebase';
import { WORKER_API_BASE_URL } from '@/lib/worker-api';

const API_BASE = WORKER_API_BASE_URL;

type SharedCostBody = {
  category_id?: string | null;
  description: string;
  amount_cents: number;
  payer_id: string;
  transaction_date?: string;
  weights: Record<string, number>;
  approve_immediately?: boolean;
};

type CorrectionBody = {
  reason: string;
  entries: Array<{ user_id: string; amount_cents: number }>;
};

async function authToken(): Promise<string> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw Object.assign(new Error('Not authenticated'), { code: 'auth/not-authenticated' });
  }
  return token;
}

async function authorizedJson(path: string, body: unknown = {}) {
  const token = await authToken();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({})) as { error?: string; message?: string };
    throw Object.assign(new Error(errorBody.message ?? `Request failed with ${response.status}`), {
      status: response.status,
      code: errorBody.error,
    });
  }

  return response.json();
}

async function authorizedText(path: string, body: unknown = {}) {
  const token = await authToken();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({})) as { error?: string; message?: string };
    throw Object.assign(new Error(errorBody.message ?? `Request failed with ${response.status}`), {
      status: response.status,
      code: errorBody.error,
    });
  }

  return response.text();
}

export function createSharedCost(body: SharedCostBody) {
  return authorizedJson('/api/entertainment/shared-costs', body);
}

export function submitSharedCost(body: SharedCostBody) {
  return authorizedJson('/api/entertainment/shared-costs', body);
}

export function approveSharedCost(transactionId: string) {
  return authorizedJson(`/api/entertainment/shared-costs/${encodeURIComponent(transactionId)}/approve`, {});
}

export function rejectSharedCost(transactionId: string, body: { reason: string }) {
  return authorizedJson(`/api/entertainment/shared-costs/${encodeURIComponent(transactionId)}/reject`, body);
}

export function deletePendingSharedCost(transactionId: string, body: { reason: string }) {
  return authorizedJson(`/api/entertainment/shared-costs/${encodeURIComponent(transactionId)}/delete`, body);
}

export function applyLedgerDraft(body: { changes: Array<{ entryId: string; amount_cents: number }>; reason?: string | null }) {
  return authorizedJson('/api/entertainment/ledger/apply-draft', body);
}

export function postLedgerCorrection(body: CorrectionBody) {
  return authorizedJson('/api/entertainment/ledger/corrections', body);
}

export function createPayment(body: { payer_id: string; payee_id: string; amount_cents: number }) {
  return authorizedJson('/api/entertainment/payments', body);
}

export function confirmPayerPayment(paymentId: string) {
  return authorizedJson(`/api/entertainment/payments/${encodeURIComponent(paymentId)}/payer-confirm`, {});
}

export function acknowledgePayeePayment(paymentId: string) {
  return authorizedJson(`/api/entertainment/payments/${encodeURIComponent(paymentId)}/payee-acknowledge`, {});
}

export function rejectPayment(paymentId: string, body: { reason: string }) {
  return authorizedJson(`/api/entertainment/payments/${encodeURIComponent(paymentId)}/reject`, body);
}

export function exportLedgerCsv() {
  return authorizedText('/api/entertainment/ledger/export');
}

export function exportBalancesCsv() {
  return authorizedText('/api/entertainment/balances/export');
}
